import { useLayoutEffect, useRef } from "react";
import { INTRO, bayer, revealFront } from "./intro";

const BG = [10, 10, 10];
const SILHOUETTE = [12, 12, 20]; // the hill colour, so the landing is seamless
const TRAIL = [244, 236, 214];
const FRONT = [0, 0, 0]; // dark band along the edge of the reveal

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const easeInOut = (k) => (k < 0.5 ? 4 * k * k * k : 1 - (-2 * k + 2) ** 3 / 2);

// The opening: a big pixel hen in the middle of the screen hops onto its hill,
// then the whole page, scene and email alike, dissolves in outward from where it lands.
function HenIntro({ hen, clock, onDone }) {
  const canvasRef = useRef(null);
  const henRef = useRef(hen);
  henRef.current = hen;

  // Layout effect, so the curtain is painted before the page underneath is ever shown
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { w, h } = henRef.current;
    const order = Array.from({ length: w * h }, () => Math.random());
    const trail = [];
    let raf = 0;
    let lastTrail = 0;

    // Low-res curtain on the scene's own pixel grid, scaled up with hard edges
    const curtain = document.createElement("canvas");
    const curtainCtx = curtain.getContext("2d");
    let curtainImage = null;

    const size = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const cell = henRef.current.cell;
      curtain.width = Math.ceil(window.innerWidth / cell) + 2;
      curtain.height = Math.ceil(window.innerHeight / cell) + 2;
      curtainImage = curtainCtx.createImageData(curtain.width, curtain.height);
    };
    size();
    window.addEventListener("resize", size);

    const drawCurtain = (e) => {
      const { left, top, cell } = henRef.current;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // Line the curtain's pixels up with the scene's pixels
      const ox = (((left % cell) + cell) % cell) - cell;
      const oy = (((top % cell) + cell) % cell) - cell;
      const cx = left + (w * cell) / 2;
      const cy = top + (h * cell) / 2;
      const far = Math.max(
        Math.hypot(cx, cy),
        Math.hypot(vw - cx, cy),
        Math.hypot(cx, vh - cy),
        Math.hypot(vw - cx, vh - cy)
      );
      const front = revealFront(e);
      const { width: cols, height: rows } = curtain;
      const data = curtainImage.data;
      for (let gy = 0; gy < rows; gy++) {
        for (let gx = 0; gx < cols; gx++) {
          const x = ox + (gx + 0.5) * cell;
          const y = oy + (gy + 0.5) * cell;
          const d = Math.hypot(x - cx, y - cy) / far + bayer(gx, gy) * 0.08;
          const i = (gy * cols + gx) * 4;
          if (d > front) {
            data[i] = BG[0];
            data[i + 1] = BG[1];
            data[i + 2] = BG[2];
            data[i + 3] = 255;
          } else if (d > front - 0.03) {
            data[i] = FRONT[0];
            data[i + 1] = FRONT[1];
            data[i + 2] = FRONT[2];
            data[i + 3] = 210;
          } else {
            data[i + 3] = 0;
          }
        }
      }
      curtainCtx.putImageData(curtainImage, 0, 0);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(curtain, ox, oy, cols * cell, rows * cell);
    };

    const drawHen = (x, y, cell, appear, silhouette) => {
      const { mask, rgba } = henRef.current;
      for (let j = 0; j < h; j++) {
        for (let i = 0; i < w; i++) {
          const m = j * w + i;
          if (!mask[m] || order[m] > appear) continue;
          const r = rgba[m * 4] + (SILHOUETTE[0] - rgba[m * 4]) * silhouette;
          const g = rgba[m * 4 + 1] + (SILHOUETTE[1] - rgba[m * 4 + 1]) * silhouette;
          const b = rgba[m * 4 + 2] + (SILHOUETTE[2] - rgba[m * 4 + 2]) * silhouette;
          ctx.fillStyle = `rgb(${r | 0}, ${g | 0}, ${b | 0})`;
          // Snap each block to whole pixels so the sprite stays crisp while it shrinks
          const px = Math.round(x + i * cell);
          const py = Math.round(y + j * cell);
          ctx.fillRect(px, py, Math.round(x + (i + 1) * cell) - px, Math.round(y + (j + 1) * cell) - py);
        }
      }
    };

    const frame = () => {
      raf = requestAnimationFrame(frame);
      const e = performance.now() - clock.start;
      const target = henRef.current;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      ctx.clearRect(0, 0, vw, vh);
      drawCurtain(e);

      const startCell = Math.max(4, Math.min(10, Math.round((Math.min(vw, vh) * 0.24) / h)));
      const cx = vw / 2 - (w * startCell) / 2;
      const cy = vh / 2 - (h * startCell) / 2;

      const k = easeInOut(clamp01((e - INTRO.flyStart) / (INTRO.flyEnd - INTRO.flyStart)));
      const cell = startCell + (target.cell - startCell) * k;
      const x = cx + (target.left - cx) * k;
      const y = cy + (target.top - cy) * k - Math.sin(k * Math.PI) * Math.min(90, vh * 0.12);
      const silhouette = clamp01((k - 0.5) / 0.5);

      // Pixels shake loose behind the hen while it travels
      if (k > 0 && k < 1 && e - lastTrail > 16) {
        lastTrail = e;
        for (let n = 0; n < 3; n++) {
          trail.push({
            x: x + Math.random() * w * cell,
            y: y + Math.random() * h * cell,
            vx: (Math.random() - 0.5) * 0.6,
            vy: 0.2 + Math.random() * 0.6,
            born: e,
            size: Math.max(2, Math.round(cell)),
          });
        }
      }
      for (let n = trail.length - 1; n >= 0; n--) {
        const p = trail[n];
        const age = (e - p.born) / 700;
        if (age >= 1) {
          trail.splice(n, 1);
          continue;
        }
        p.x += p.vx;
        p.y += p.vy;
        ctx.fillStyle = `rgba(${TRAIL[0]}, ${TRAIL[1]}, ${TRAIL[2]}, ${(1 - age) * 0.7})`;
        ctx.fillRect(Math.round(p.x), Math.round(p.y), p.size, p.size);
      }

      // Once landed, the scene draws the hen itself. Hold ours a moment longer
      // so the hand-off overlaps instead of blinking between the two canvases.
      if (e < INTRO.flyEnd + 150) {
        drawHen(x, y, cell, clamp01(e / INTRO.henIn), silhouette);
      }

      if (e >= INTRO.done) {
        cancelAnimationFrame(raf);
        onDone();
      }
    };
    frame();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", size);
    };
  }, [clock, onDone]);

  return <canvas ref={canvasRef} className="hen-intro" aria-hidden="true" />;
}

export default HenIntro;
